/**
 * Centralized API configuration
 * Single source of truth for backend API URLs
 */

// Production URL constant - single source of truth
const PRODUCTION_API_URL = 'https://aievaluator.in';

const getApiBaseUrl = () => {
  const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalBrowser = browserHost === 'localhost' || browserHost === '127.0.0.1';

  // Priority 1: Environment variable (for production/staging)
  if (process.env.REACT_APP_API_URL) {
    // A browser treats localhost and 127.0.0.1 as different sites. Keep a
    // local frontend and local backend on the same hostname so the employer
    // session cookie works during HTTP development.
    try {
      const configuredUrl = new URL(process.env.REACT_APP_API_URL);
      const configuredHostIsLocal =
        configuredUrl.hostname === 'localhost' || configuredUrl.hostname === '127.0.0.1';
      if (isLocalBrowser && configuredHostIsLocal) {
        return `${window.location.protocol}//${browserHost}${configuredUrl.port ? `:${configuredUrl.port}` : ''}`;
      }
    } catch {
      // Preserve a non-standard configured URL exactly as supplied.
    }
    return process.env.REACT_APP_API_URL;
  }

  // Priority 2: Detect hostname and route to correct backend
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;

    // Local development
    if (host === 'localhost' || host === '127.0.0.1') {
      // Use environment variable for port if available, otherwise default to 5000
      const port = process.env.REACT_APP_API_PORT || '5000';
      return `http://${host}:${port}`;
    }

    // Staging (catapulyst.tech) → Web-Interviewer staging
    if (host.endsWith('catapulyst.tech')) {
      return 'https://aievaluator.catapulyst.tech';
    }

    // Production (searchjob.ai) → Web-Interviewer production
    if (host.endsWith('searchjob.ai')) {
      return 'https://aievaluator.in';
    }
  }

  // Priority 3: Production fallback
  return PRODUCTION_API_URL;
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Helper function to build full API URL
 * @param {string} endpoint - API endpoint (e.g., '/api/users')
 * @returns {string} Full URL
 */
export const getApiUrl = (endpoint) => {
  // If endpoint already includes protocol, return as-is
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_BASE_URL}${normalizedEndpoint}`;
};

/**
 * Helper function to resolve API URL with fallback to production
 * Returns array of URLs to try (local first, then production)
 */
export const getApiUrlsWithFallback = (endpoint) => {
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const localUrl = getApiUrl(normalizedEndpoint);
  const productionUrl = `${PRODUCTION_API_URL}${normalizedEndpoint}`;
  
  // If we're already using production, just return that
  if (API_BASE_URL === PRODUCTION_API_URL) {
    return [productionUrl];
  }
  
  // Otherwise, try local first, then production
  return [localUrl, productionUrl];
};
